// corteHelpers.js — El corte de forma con el que una sección se despide de la
// siguiente: la diagonal del bloque azul de HepCampus contra el blanco, una
// curva, un pico…
//
// Se pinta como una franja absoluta pegada al borde de la sección, con un SVG
// del color del fondo *siguiente*. Va como imagen de fondo (no `clip-path`)
// porque así el corte no recorta el contenido de la sección y en móvil no
// aparecen los dientes de sierra que deja el recorte al hacer scroll.
//
// Claves de config que lee (todas opcionales; sin `corte_inferior` /
// `corte_superior` no se pinta nada y la sección queda como siempre):
//   corte_inferior / corte_superior          forma (ver FORMAS_CORTE)
//   corte_color / corte_superior_color       color del corte (el del fondo vecino)
//   corte_alto / corte_superior_alto         alto de la franja  (default 51px)
//   corte_alto_movil / corte_superior_alto_movil  alto en móvil (default: la mitad)

// Cada forma es el contenido del <svg viewBox="0 0 1280 140"> ya con el color.
// El área pintada es la que "se come" el fondo vecino.
const PATHS = {
  // Diagonal que deja la sección más alta a la derecha (la de HepCampus).
  diagonal_der: 'M0 0v140h1280L0 0z',
  // La misma diagonal al revés: la sección queda más alta a la izquierda.
  diagonal_izq: 'M1280 0v140H0L1280 0z',
  // Pico central hacia adentro.
  pico: 'M0 140h1280L640 0 0 140z',
  // Pico central hacia afuera.
  pico_invertido: 'M0 0h1280v140L640 40 0 140V0z',
  // Curva suave (el fondo vecino sube por los lados).
  curva: 'M0 140C320 40 960 40 1280 140V0H0v140z',
  // Curva al revés (el fondo vecino baja en el centro).
  curva_invertida: 'M0 0c320 100 960 100 1280 0v140H0V0z',
};

export const FORMAS_CORTE = [
  { value: '', label: 'Sin corte (recto)' },
  { value: 'diagonal_der', label: 'Diagonal ↗ (más alto a la derecha)' },
  { value: 'diagonal_izq', label: 'Diagonal ↖ (más alto a la izquierda)' },
  { value: 'curva', label: 'Curva' },
  { value: 'curva_invertida', label: 'Curva invertida' },
  { value: 'pico', label: 'Pico hacia adentro' },
  { value: 'pico_invertido', label: 'Pico hacia afuera' },
];

// SVG → data URI. Se codifica con encodeURIComponent (no base64) para que el
// color se lea tal cual en el inspector y sea fácil de depurar.
const svgUrl = (forma, color, invertirY) => {
  const d = PATHS[forma];
  if (!d) return null;
  const transform = invertirY ? ' transform="scale(1,-1) translate(0,-140)"' : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="140" viewBox="0 0 1280 140" preserveAspectRatio="none">` +
    `<path d="${d}" fill="${color}"${transform}/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

// Mitad del alto (redondeada) para móvil, respetando la unidad configurada.
const altoMovil = (alto) => {
  const m = String(alto).match(/^([\d.]+)(px|rem|em|vw|vh|%)?$/);
  if (!m) return alto;
  const n = Math.max(1, Math.round(parseFloat(m[1]) / 2));
  return `${n}${m[2] || 'px'}`;
};

// CSS de una franja (::before arriba, ::after abajo). Devuelve '' si la sección
// no pidió corte, para no emitir reglas de más.
const franja = (pseudo, forma, color, alto, altoMov) => {
  const url = svgUrl(forma, color || '#ffffff', pseudo === 'before');
  if (!url) return '';
  const h = alto || '51px';
  return `
    &::${pseudo} {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      ${pseudo === 'before' ? 'top: 0;' : 'bottom: 0;'}
      height: ${h};
      background-image: ${url};
      background-size: 100% 100%;
      background-repeat: no-repeat;
      pointer-events: none;
      z-index: 2;
      @media (max-width: 600px) { height: ${altoMov || altoMovil(h)}; }
    }
  `;
};

// ¿Esta sección pidió algún corte?
export const tieneCorte = (c = {}) => !!(PATHS[c.corte_inferior] || PATHS[c.corte_superior]);

// CSS completo del envoltorio con cortes (lo consume SectionRenderer).
export const corteCss = (c = {}) => {
  if (!tieneCorte(c)) return '';
  return (
    franja('after', c.corte_inferior, c.corte_color, c.corte_alto, c.corte_alto_movil) +
    franja('before', c.corte_superior, c.corte_superior_color, c.corte_superior_alto, c.corte_superior_alto_movil)
  );
};

export default corteCss;
